/**
 * Org Profile sub-panel — name, founded, contact, full mailing address,
 * tax ID (EIN), logo upload, social links.
 *
 * Backed by:
 *   GET  /api/organizations/{org_id}              — load
 *   PUT  /api/organizations/{org_id}              — save (uses OrganizationUpdate)
 *   POST /api/organizations/{org_id}/logo         — multipart logo upload
 *
 * The form is "save the whole thing on click" rather than autosave per
 * field — ADs typically tweak several fields together (e.g. updating the
 * address right after a move) and a single Save is much less surprising.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2, Save, Upload, Building2, MapPin, Hash, Globe, AlertCircle, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_CONTACT = {
  email: '', phone: '', address1: '', address2: '', city: '', state: '',
  postal_code: '', country: 'USA', website: '', facebook: '', instagram: '', twitter: '',
};
const EMPTY_ADDR = { address1: '', address2: '', city: '', state: '', postal_code: '', country: 'USA' };
const EMPTY_FIN = {
  legal_name: '', ein: '', industry: '', business_type: '', business_type_options: [],
  fiscal_year_start_month: 1, legal_address: { ...EMPTY_ADDR },
  customer_contact: { email: '', address: { ...EMPTY_ADDR } },
};
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const ADDR_KEYS = ['address1', 'address2', 'city', 'state', 'postal_code', 'country'];

// The mailing address lives on the org `contact`; Finance keeps its own copies
// (legal address, customer-contact address). When they match — the usual case —
// the page shows ONE address and saves it into all three.
const pickAddr = (src) => ADDR_KEYS.reduce((a, k) => ({ ...a, [k]: (src || {})[k] || '' }), {});
const sameAddr = (a, b) => ADDR_KEYS.filter(k => k !== 'country')
  .every(k => ((a || {})[k] || '').trim().toLowerCase() === ((b || {})[k] || '').trim().toLowerCase());
const blankAddr = (a) => ADDR_KEYS.filter(k => k !== 'country').every(k => !((a || {})[k] || '').trim());

function AddressFields({ value, onChange, inputCls, labelCls, testid }) {
  return (
    <div className="space-y-3">
      <div>
        <Label className={labelCls}>Address line 1</Label>
        <Input value={value.address1} onChange={(e) => onChange('address1', e.target.value)}
               className={inputCls} data-testid={`${testid}-addr1`} />
      </div>
      <div>
        <Label className={labelCls}>Address line 2 (optional)</Label>
        <Input value={value.address2} onChange={(e) => onChange('address2', e.target.value)}
               className={inputCls} data-testid={`${testid}-addr2`} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        <div className="md:col-span-2">
          <Label className={labelCls}>City</Label>
          <Input value={value.city} onChange={(e) => onChange('city', e.target.value)}
                 className={inputCls} data-testid={`${testid}-city`} />
        </div>
        <div>
          <Label className={labelCls}>State</Label>
          <Input value={value.state} onChange={(e) => onChange('state', e.target.value)}
                 className={inputCls} data-testid={`${testid}-state`} />
        </div>
        <div>
          <Label className={labelCls}>Postal code</Label>
          <Input value={value.postal_code} onChange={(e) => onChange('postal_code', e.target.value)}
                 className={inputCls} data-testid={`${testid}-zip`} />
        </div>
        <div>
          <Label className={labelCls}>Country</Label>
          <Input value={value.country} onChange={(e) => onChange('country', e.target.value)}
                 className={inputCls} data-testid={`${testid}-country`} />
        </div>
      </div>
    </div>
  );
}

export default function OrgProfilePanel({ orgId, isDark, showContact = true, showIdentity = true }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState({ text: '', type: '' });
  const [form, setForm] = useState({
    name: '', founding_year: 2020, tagline: '', tax_id: '', logo_url: '',
    sports: [], divisions: [],
    contact: { ...EMPTY_CONTACT },
  });
  const [fin, setFin] = useState(null);
  const [legalSame, setLegalSame] = useState(true);
  const [ccSame, setCcSame] = useState(true);
  const fileRef = useRef(null);

  const showMsg = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: '', type: '' }), 3500);
  };

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const orgRes = await axios.get(`${API_URL}/api/organizations/${orgId}`);
      const o = orgRes.data || {};
      setForm({
        name: o.name || '',
        founding_year: o.founding_year || new Date().getFullYear(),
        tagline: o.tagline || '',
        tax_id: o.tax_id || '',
        logo_url: o.logo_url || '',
        sports: o.sports || [],
        divisions: o.divisions || [],
        contact: { ...EMPTY_CONTACT, ...(o.contact || {}) },
      });
      try {
        const fp = (await axios.get(`${API_URL}/api/organizations/${orgId}/finance-profile`)).data || {};
        const legal = { ...EMPTY_ADDR, ...(fp.legal_address || {}) };
        const ccAddr = { ...EMPTY_ADDR, ...((fp.customer_contact || {}).address || {}) };
        const mailing = o.contact || {};
        setFin({
          ...EMPTY_FIN, ...fp,
          legal_name: fp.legal_name || o.name || '',
          legal_address: legal,
          customer_contact: { email: (fp.customer_contact || {}).email || '', address: ccAddr },
        });
        setLegalSame(blankAddr(legal) || sameAddr(legal, mailing));
        setCcSame(blankAddr(ccAddr) || sameAddr(ccAddr, mailing));
      } catch { setFin({ ...EMPTY_FIN }); }
    } catch (err) {
      console.error('Org load failed:', err);
      showMsg(err.response?.data?.detail || 'Failed to load organization', 'error');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const setContact = (k, v) => setForm(prev => ({ ...prev, contact: { ...prev.contact, [k]: v } }));
  const setFinField = (k, v) => setFin(prev => ({ ...prev, [k]: v }));
  const setFinAddr = (k, v) => setFin(prev => ({ ...prev, legal_address: { ...prev.legal_address, [k]: v } }));
  const setFinCC = (k, v) => setFin(prev => ({ ...prev, customer_contact: { ...prev.customer_contact, [k]: v } }));
  const setFinCCAddr = (k, v) => setFin(prev => ({ ...prev, customer_contact: { ...prev.customer_contact, address: { ...prev.customer_contact.address, [k]: v } } }));

  const handleLogoUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showMsg('Please select an image file', 'error');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API_URL}/api/organizations/${orgId}/logo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setField('logo_url', data.logo_url);
      showMsg('Logo uploaded', 'success');
    } catch (err) {
      showMsg(err.response?.data?.detail || 'Logo upload failed', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        founding_year: parseInt(form.founding_year, 10) || new Date().getFullYear(),
        tagline: form.tagline || null,
        tax_id: form.tax_id || null,
        logo_url: form.logo_url || null,
        contact: form.contact,
      };
      await axios.put(`${API_URL}/api/organizations/${orgId}`, payload);
      if (fin && showIdentity) {
        // "Same as mailing" stores NO separate address — the backend then always
        // follows the org mailing address (one source, no stale copies).
        await axios.put(`${API_URL}/api/organizations/${orgId}/finance-profile`, {
          legal_name: fin.legal_name || form.name || '', industry: fin.industry || '',
          business_type: fin.business_type || '',
          fiscal_year_start_month: parseInt(fin.fiscal_year_start_month, 10) || 1,
          legal_address: legalSame ? null : pickAddr(fin.legal_address),
          customer_contact: {
            email: fin.customer_contact.email || '',
            address: ccSame ? null : pickAddr(fin.customer_contact.address),
          },
        });
      }
      showMsg('Organization profile saved', 'success');
    } catch (err) {
      showMsg(err.response?.data?.detail || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
        <CardContent className="p-12 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
        </CardContent>
      </Card>
    );
  }

  const cardCls = isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200';
  const inputCls = isDark ? 'bg-slate-700/50 border-slate-600 text-white' : '';
  const labelCls = `text-xs font-medium ${isDark ? 'text-slate-300' : 'text-slate-600'}`;
  const sectionTitleCls = `text-sm font-semibold uppercase tracking-wider ${isDark ? 'text-amber-400' : 'text-amber-700'}`;

  return (
    <div className="space-y-4" data-testid="org-profile-panel">
      {msg.text && (
        <div className={`rounded-lg p-3 text-sm flex items-center gap-2 ${msg.type === 'error' ? 'bg-red-500/10 border border-red-500/40 text-red-400' : 'bg-emerald-500/10 border border-emerald-500/40 text-emerald-400'}`}
             data-testid="org-profile-msg">
          {msg.type === 'error' ? <AlertCircle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
          {msg.text}
        </div>
      )}

      {/* Identity + logo — gated by `showIdentity` (Session 19, May 12 2026).
          The AD's Organisations hub uses the default (showIdentity=true).
          Tech Settings → Org Profile passes showIdentity=false because the
          TS only needs to edit Contact / Website / Social. */}
      {showIdentity && (
      <Card className={cardCls}>
        <CardHeader>
          <CardTitle className={`flex items-center gap-2 text-base ${isDark ? 'text-white' : 'text-slate-900'}`}>
            <Building2 className="h-5 w-5 text-amber-500" />
            Identity
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Logo */}
            <div className="flex flex-col items-center gap-2 md:w-48">
              <div className={`relative h-32 w-32 rounded-xl border-2 border-dashed flex items-center justify-center overflow-hidden ${isDark ? 'border-slate-600 bg-slate-700/40' : 'border-slate-300 bg-slate-50'}`}>
                {form.logo_url ? (
                  <img src={form.logo_url.startsWith('http') ? form.logo_url : `${API_URL}${form.logo_url}`}
                       alt="Org logo" className="h-full w-full object-contain"
                       data-testid="org-logo-preview" />
                ) : (
                  <Building2 className={`h-12 w-12 ${isDark ? 'text-slate-600' : 'text-slate-300'}`} />
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                     onChange={(e) => handleLogoUpload(e.target.files?.[0])}
                     data-testid="org-logo-input" />
              <Button size="sm" variant="outline" disabled={uploading}
                      onClick={() => fileRef.current?.click()}
                      data-testid="org-logo-upload-btn">
                {uploading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Upload className="h-3 w-3 mr-1" />}
                {form.logo_url ? 'Replace logo' : 'Upload logo'}
              </Button>
              <p className={`text-[10px] ${isDark ? 'text-slate-500' : 'text-slate-400'} text-center`}>
                Square works best · PNG / JPG / WebP · 5 MB max · stored as WebP
              </p>
            </div>

            {/* Identity fields */}
            <div className="flex-1 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className={labelCls}>Organization name</Label>
                  <Input value={form.name} onChange={(e) => setField('name', e.target.value)}
                         className={inputCls} data-testid="org-name-input" />
                </div>
                {fin && (
                  <div>
                    <Label className={labelCls}>Legal name</Label>
                    <Input value={fin.legal_name} onChange={(e) => setFinField('legal_name', e.target.value)}
                           placeholder={form.name} className={inputCls} data-testid="fin-legal-name" />
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className={labelCls}>Founded</Label>
                  <Input type="number" value={form.founding_year}
                         onChange={(e) => setField('founding_year', e.target.value)}
                         className={inputCls} data-testid="org-founded-input" />
                </div>
                <div>
                  <Label className={labelCls}>Tax ID / EIN</Label>
                  <Input value={form.tax_id} onChange={(e) => setField('tax_id', e.target.value)}
                         placeholder="00-0000000" className={inputCls}
                         data-testid="org-tax-id-input" />
                </div>
              </div>
              <div>
                <Label className={labelCls}>Tagline</Label>
                <Input value={form.tagline} onChange={(e) => setField('tagline', e.target.value)}
                       placeholder="A short one-liner shown on the public site"
                       className={inputCls} data-testid="org-tagline-input" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Address — ONE address for the org (stored on `contact`). Finance's legal
          and customer-contact addresses default to the same; untick to set them
          separately. Gated by showIdentity (AD surface, not the TS web/social one). */}
      {showIdentity && (
      <Card className={cardCls}>
        <CardHeader>
          <CardTitle className={`flex items-center gap-2 text-base ${isDark ? 'text-white' : 'text-slate-900'}`}>
            <MapPin className="h-5 w-5 text-amber-500" />
            Address
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <AddressFields value={form.contact} onChange={setContact}
                         inputCls={inputCls} labelCls={labelCls} testid="org" />
          {fin && (
            <div className="space-y-3 pt-1">
              <label className={`flex items-center gap-2 text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                <input type="checkbox" checked={legalSame} onChange={(e) => setLegalSame(e.target.checked)}
                       data-testid="fin-legal-same" />
                Legal address is the same
              </label>
              {!legalSame && (
                <div className="pl-6 space-y-2">
                  <div className={sectionTitleCls}>Legal address</div>
                  <AddressFields value={fin.legal_address} onChange={setFinAddr}
                                 inputCls={inputCls} labelCls={labelCls} testid="fin-legal" />
                </div>
              )}
              <label className={`flex items-center gap-2 text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                <input type="checkbox" checked={ccSame} onChange={(e) => setCcSame(e.target.checked)}
                       data-testid="fin-cc-same" />
                Customer contact address is the same
              </label>
              {!ccSame && (
                <div className="pl-6 space-y-2">
                  <div className={sectionTitleCls}>Customer contact address</div>
                  <AddressFields value={fin.customer_contact.address} onChange={setFinCCAddr}
                                 inputCls={inputCls} labelCls={labelCls} testid="fin-cc" />
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Finance settings (Step 8B §7) — used by OL Finance (reports, exports,
          invoices). Legal name + EIN live in Identity; addresses in Address. */}
      {showIdentity && fin && (
      <Card className={cardCls} data-testid="org-finance-profile">
        <CardHeader>
          <CardTitle className={`flex items-center gap-2 text-base ${isDark ? 'text-white' : 'text-slate-900'}`}>
            <Hash className="h-5 w-5 text-amber-500" />
            Finance settings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className={labelCls}>Industry</Label>
              <Input value={fin.industry} onChange={(e) => setFinField('industry', e.target.value)}
                     className={inputCls} data-testid="fin-industry" />
            </div>
            <div>
              <Label className={labelCls}>Business type</Label>
              <select value={fin.business_type} onChange={(e) => setFinField('business_type', e.target.value)}
                      data-testid="fin-business-type"
                      className={`w-full h-10 rounded-md border px-3 text-sm ${isDark ? 'bg-slate-700/50 border-slate-600 text-white' : 'border-slate-300'}`}>
                <option value="">Select…</option>
                {(fin.business_type_options || []).map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <Label className={labelCls}>Fiscal year starts</Label>
              <select value={fin.fiscal_year_start_month} onChange={(e) => setFinField('fiscal_year_start_month', e.target.value)}
                      data-testid="fin-fiscal-year"
                      className={`w-full h-10 rounded-md border px-3 text-sm ${isDark ? 'bg-slate-700/50 border-slate-600 text-white' : 'border-slate-300'}`}>
                {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
              </select>
            </div>
            <div>
              <Label className={labelCls}>Customer-facing email</Label>
              <Input type="email" value={fin.customer_contact.email} onChange={(e) => setFinCC('email', e.target.value)}
                     className={inputCls} data-testid="fin-cc-email" />
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Contact + web — Session 19 (May 12 2026): visibility gated by
          `showContact`. The AD's Organisations hub passes `showContact={false}`
          because these fields moved to Tech Settings → Site Linking →
          Org Profile (Tech-Specialist responsibility). */}
      {showContact && (
      <Card className={cardCls}>
        <CardHeader>
          <CardTitle className={`flex items-center gap-2 text-base ${isDark ? 'text-white' : 'text-slate-900'}`}>
            <Globe className="h-5 w-5 text-amber-500" />
            Contact &amp; web
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className={labelCls}>Public email</Label>
              <Input type="email" value={form.contact.email}
                     onChange={(e) => setContact('email', e.target.value)}
                     className={inputCls} data-testid="org-email-input" />
            </div>
            <div>
              <Label className={labelCls}>Phone</Label>
              <Input value={form.contact.phone}
                     onChange={(e) => setContact('phone', e.target.value)}
                     className={inputCls} data-testid="org-phone-input" />
            </div>
            <div>
              <Label className={labelCls}>Website</Label>
              <Input value={form.contact.website}
                     onChange={(e) => setContact('website', e.target.value)}
                     placeholder="https://"
                     className={inputCls} data-testid="org-website-input" />
            </div>
            <div>
              <Label className={labelCls}>Facebook</Label>
              <Input value={form.contact.facebook}
                     onChange={(e) => setContact('facebook', e.target.value)}
                     className={inputCls} data-testid="org-facebook-input" />
            </div>
            <div>
              <Label className={labelCls}>Instagram</Label>
              <Input value={form.contact.instagram}
                     onChange={(e) => setContact('instagram', e.target.value)}
                     className={inputCls} data-testid="org-instagram-input" />
            </div>
            <div>
              <Label className={labelCls}>X / Twitter</Label>
              <Input value={form.contact.twitter}
                     onChange={(e) => setContact('twitter', e.target.value)}
                     className={inputCls} data-testid="org-twitter-input" />
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving || !form.name}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="org-profile-save-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
          Save profile
        </Button>
      </div>
    </div>
  );
}
